import Store from 'repatch';

import State from '../core/State';
import { demoProjectData } from '../data/demo_project/project';
import PageInfo from '../entity/PageInfo';
import { ProjectFilterKey } from '../entity/ProjectFilter';
import ProjectOrderTarget from '../entity/ProjectOrderTarget';
import { ProjectForAllOfOrganizationFragment } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import OrganizationWebApi from '../service/persistent/OrganizationWebApi';
import ProjectWebApi from '../service/persistent/ProjectWebApi';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import {
  appendProjects,
  disposeProjects,
  finishFetchingProjects,
  setHasDraftProjects,
  setProjects,
  startFetchingProjects,
} from '../thunk/projects';
import { OrganizationDetailFragment } from './../generated/graphql';

class ListProjectUsecase {
  private store: Store<State>;
  private errorPresenter: ErrorPresenter;
  private organizationWebApi: OrganizationWebApi;
  private projectWebApi: ProjectWebApi;

  constructor({
    store,
    errorPresenter,
    organizationWebApi,
    projectWebApi,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    organizationWebApi: OrganizationWebApi;
    projectWebApi: ProjectWebApi;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.organizationWebApi = organizationWebApi;
    this.projectWebApi = projectWebApi;
  }

  public prepareProjects = async (organizationId: string): Promise<void> => {
    this.store.dispatch(startFetchingProjects('draft'));
    this.store.dispatch(startFetchingProjects('notDraft'));
    this.store.dispatch(startFetchingProjects('finalized'));

    let organization: OrganizationDetailFragment;
    let draftProjects: { projects: Array<ProjectForAllOfOrganizationFragment>; pageInfo: PageInfo };
    let notDraftProjects: { projects: Array<ProjectForAllOfOrganizationFragment>; pageInfo: PageInfo };
    let finalizedProjects: { projects: Array<ProjectForAllOfOrganizationFragment>; pageInfo: PageInfo };

    try {
      [organization, draftProjects, notDraftProjects, finalizedProjects] = await Promise.all([
        this.organizationWebApi.getById(organizationId),
        this.projectWebApi.getAllOfOrganization(organizationId, 'draft', 'updatedAt'),
        this.projectWebApi.getAllOfOrganization(organizationId, 'notDraft', 'submittedAt'),
        this.projectWebApi.getAllOfOrganization(organizationId, 'finalized', 'submittedAt'),
      ]);
    } catch (err) {
      this.store.dispatch(finishFetchingProjects('draft'));
      this.store.dispatch(finishFetchingProjects('notDraft'));
      this.store.dispatch(disposeProjects());
      this.errorPresenter.showAsFatal(err);

      return;
    }
    const hasDraftItems = notDraftProjects.projects
      .filter((project) => {
        const localProject = JSON.parse(window.localStorage.getItem(`project_brief_${project.id}`) || '{}');
        return Object.keys(localProject).length;
      })
      .map((project) => project.id);

    const demoProjects = demoProjectData.map((dp) => dp.data.project as ProjectForAllOfOrganizationFragment);
    notDraftProjects =
      !notDraftProjects.pageInfo.hasNextPage && organization.businessRegion.key === 'jp'
        ? { ...notDraftProjects, projects: [...notDraftProjects.projects, ...demoProjects] }
        : notDraftProjects;

    this.store.dispatch(setHasDraftProjects(hasDraftItems));
    this.store.dispatch(setLastAccessedOrganization(organization));
    this.store.dispatch(setProjects('draft', draftProjects.projects, draftProjects.pageInfo));
    this.store.dispatch(finishFetchingProjects('draft'));
    this.store.dispatch(setProjects('notDraft', notDraftProjects.projects, notDraftProjects.pageInfo));
    this.store.dispatch(finishFetchingProjects('notDraft'));
    this.store.dispatch(setProjects('finalized', finalizedProjects.projects, finalizedProjects.pageInfo));
    this.store.dispatch(finishFetchingProjects('finalized'));
  };

  public loadMoreProjects = async (
    organizationId: string,
    key: ProjectFilterKey,
    projectOrderTarget: ProjectOrderTarget,
    cursor: string,
  ): Promise<void> => {
    this.store.dispatch(startFetchingProjects(key));
    let res: { projects: Array<ProjectForAllOfOrganizationFragment>; pageInfo: PageInfo };
    try {
      res = await this.projectWebApi.getAllOfOrganization(organizationId, key, projectOrderTarget, cursor);
    } catch (err) {
      this.store.dispatch(finishFetchingProjects(key));
      this.errorPresenter.showAsFatal(err);

      return;
    }

    const demoProjects = demoProjectData.map((dp) => dp.data.project as ProjectForAllOfOrganizationFragment);
    const projects =
      !res.pageInfo.hasNextPage && this.store.getState().lastAccessedOrganization!.businessRegion.key === 'jp'
        ? [...res.projects, ...demoProjects]
        : res.projects;
    this.store.dispatch(appendProjects(key, projects, res.pageInfo));
    this.store.dispatch(finishFetchingProjects(key));
  };

  public getProjectsSortedInNewOrder = async (
    organizationId: string,
    projectOrderTarget: ProjectOrderTarget,
  ): Promise<void> => {
    this.disposeProjects();
    this.store.dispatch(startFetchingProjects('draft'));
    this.store.dispatch(startFetchingProjects('notDraft'));
    this.store.dispatch(startFetchingProjects('finalized'));
    let draftProjects: { projects: Array<ProjectForAllOfOrganizationFragment>; pageInfo: PageInfo };
    let notDraftProjects: { projects: Array<ProjectForAllOfOrganizationFragment>; pageInfo: PageInfo };
    let finalizedProjects: { projects: Array<ProjectForAllOfOrganizationFragment>; pageInfo: PageInfo };

    try {
      [draftProjects, notDraftProjects, finalizedProjects] = await Promise.all([
        this.projectWebApi.getAllOfOrganization(organizationId, 'draft', 'updatedAt'),
        this.projectWebApi.getAllOfOrganization(organizationId, 'notDraft', projectOrderTarget),
        this.projectWebApi.getAllOfOrganization(organizationId, 'finalized', projectOrderTarget),
      ]);
    } catch (err) {
      this.store.dispatch(finishFetchingProjects('draft'));
      this.store.dispatch(finishFetchingProjects('notDraft'));
      this.store.dispatch(disposeProjects());
      this.errorPresenter.showAsFatal(err);
      return;
    }

    const demoProjects = demoProjectData.map((dp) => dp.data.project as ProjectForAllOfOrganizationFragment);
    const projects =
      !notDraftProjects.pageInfo.hasNextPage &&
      this.store.getState().lastAccessedOrganization!.businessRegion.key === 'jp'
        ? [...notDraftProjects.projects, ...demoProjects]
        : notDraftProjects.projects;

    this.store.dispatch(setProjects('draft', draftProjects.projects, draftProjects.pageInfo));
    this.store.dispatch(finishFetchingProjects('draft'));
    this.store.dispatch(setProjects('notDraft', projects, notDraftProjects.pageInfo));
    this.store.dispatch(finishFetchingProjects('notDraft'));
    this.store.dispatch(setProjects('finalized', finalizedProjects.projects, finalizedProjects.pageInfo));
    this.store.dispatch(finishFetchingProjects('finalized'));
  };

  public disposeProjects = (): void => {
    this.store.dispatch(disposeProjects());
  };
}

export default ListProjectUsecase;
